// ==================== ENUMS ====================

export enum ReferralStage {
  INTAKE = 'INTAKE',
  PROCESSING = 'PROCESSING',
  DECISION = 'DECISION',
  EXECUTION = 'EXECUTION',
}

export enum ReferralStatus {
  // Intake
  RECEIVED = 'RECEIVED',
  // Processing
  PROCESSING = 'PROCESSING',
  PROCESSING_COMPLETE = 'PROCESSING_COMPLETE',
  AWAITING_INFO = 'AWAITING_INFO',
  // Decision
  RECOMMENDATION_READY = 'RECOMMENDATION_READY',
  PENDING_REVIEW = 'PENDING_REVIEW',
  // Execution
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  AUTO_DECLINED = 'AUTO_DECLINED',
  // Special
  BLOCKED = 'BLOCKED',
}

export enum ReferralSource {
  EMAIL = 'EMAIL',
  EFAX = 'EFAX',
  FAX = 'FAX',
  PHONE = 'PHONE',
  API = 'API',
}

export enum IngestionType {
  AUTO = 'AUTO',
  ASSISTED = 'ASSISTED',
}

export enum DecisionOutcome {
  ACCEPT = 'ACCEPT',
  DECLINE = 'DECLINE',
  PENDING = 'PENDING',
  OVERRIDE_ACCEPT = 'OVERRIDE_ACCEPT',
  OVERRIDE_DECLINE = 'OVERRIDE_DECLINE',
}

export enum FlagSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  BLOCKING = 'BLOCKING',
}

// ==================== INTERFACES ====================

export interface ReferralPatient {
  name: string;
  dob: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
  insurance: string;
  insuranceMemberId?: string;
  diagnosisCodes: string[];
  diagnosisLabels: string[];
  requestedProgram: string;
  referringProvider: string;
  referringFacility: string;
  referringPhone?: string;
  referringFax?: string;
}

export interface ReferralTimelineEvent {
  id: string;
  timestamp: string;
  type:
    | 'RECEIVED'
    | 'PROCESSING'
    | 'FLAG'
    | 'OUTREACH'
    | 'RESPONSE'
    | 'DECISION'
    | 'EXECUTION'
    | 'OVERRIDE';
  actor: 'SYSTEM' | 'AI' | string;
  label: string;
  detail?: string;
}

export interface ReferralFlag {
  id: string;
  severity: FlagSeverity;
  label: string;
  detail?: string;
}

export interface MissingField {
  field: string;
  label: string;
  required: boolean;
  aiOutreachSent: boolean;
  outreachTimestamp?: string;
  resolved: boolean;
  resolvedValue?: string;
}

export interface DecisionFactor {
  criterion: string;
  requirement: string;
  patientValue: string;
  result: 'PASS' | 'FAIL' | 'WARN' | 'UNKNOWN';
}

export interface CapacityInfo {
  territory: string;
  available: boolean;
  availableCount: number;
  recommendation: 'ACCEPT' | 'DELAYED_START' | 'HOLD';
  recommendationLabel: string;
}

export interface ExecutionStep {
  label: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETE' | 'FAILED';
  timestamp?: string;
}

export type ReferralDocumentType =
  | 'Referral Order'
  | 'Clinical Notes'
  | 'Wound Assessment'
  | 'Discharge Summary'
  | 'Lab Results'
  | 'Insurance Authorization'
  | 'Other';

export interface ReferralDocument {
  id: string;
  type: ReferralDocumentType;
  fileName: string;
  receivedAt: string;
  source: 'FAX' | 'EFAX' | 'EMAIL' | 'API' | 'UPLOADED';
  matchConfidence: 'HIGH' | 'MEDIUM';
  matchedOn: string[];
  simulatedContent: string;
  isNew?: boolean;
}

export interface PriorEncounter {
  id: string;
  date: string;
  program: string;
  outcome: 'ACCEPTED' | 'DECLINED' | 'COMPLETED';
  declineReason?: string;
  summary: string;
  assignedProvider?: string;
}

export interface ReprocessingTask {
  id: string;
  action: string;
  detail: string;
  status: 'IN_PROGRESS' | 'COMPLETE';
  completedAt?: string;
}

export interface Referral {
  id: string;
  stage: ReferralStage;
  status: ReferralStatus;
  source: ReferralSource;
  ingestionType: IngestionType;
  receivedAt: string;
  updatedAt: string;
  patient: ReferralPatient;
  processingComplete: boolean;
  flags: ReferralFlag[];
  missingFields: MissingField[];
  summary: string;
  decisionFactors: DecisionFactor[];
  capacityInfo: CapacityInfo;
  aiRecommendation: DecisionOutcome;
  aiRecommendationReason: string;
  finalDecision?: DecisionOutcome;
  finalDecisionBy?: string;
  finalDecisionAt?: string;
  overrideReason?: string;
  timeline: ReferralTimelineEvent[];
  executionSteps?: ExecutionStep[];
  // Phone intake caller context
  callerName?: string;
  callerRelationship?: string;
  // Uploaded document URL (fax/PDF intake only — object URL, session-scoped)
  uploadedFileUrl?: string;
  uploadedFileName?: string;
  // Return-for-review reprocessing
  reprocessingNote?: string;
  reprocessingTasks?: ReprocessingTask[];
  // Prior encounter history — populated when patient is matched to an existing record
  priorEncounters?: PriorEncounter[];
  // Additional matched documents received after initial intake
  additionalDocuments?: ReferralDocument[];
  // Auto-decline
  autoDeclined?: boolean;
  autoDeclineReason?: string;
  // Outreach to referring provider (decline notification)
  // In prod: outreachScheduledAt respects business hours of the receiving org's timezone
  outreachStatus?: 'PENDING' | 'SENT';
  outreachScheduledAt?: string;
  outreachSentAt?: string;
  // Demo metadata
  scenarioId?: string;
}

// ==================== INTAKE FORMS ====================

export type CallerRelationship =
  | 'Referring Provider'
  | "Referring Provider's Staff"
  | 'Patient'
  | "Patient's Family Member"
  | "Patient's Legal Guardian"
  | 'Care Manager / Social Worker'
  | 'Other';

export const CALLER_RELATIONSHIP_OPTIONS: CallerRelationship[] = [
  'Referring Provider',
  "Referring Provider's Staff",
  'Patient',
  "Patient's Family Member",
  "Patient's Legal Guardian",
  'Care Manager / Social Worker',
  'Other',
];

export interface PhoneIntakeFormData {
  callerName: string;
  callerRelationship: CallerRelationship | '';
  patientName: string;
  dob: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  diagnosisLabel: string;
  diagnosisCode: string;
  requestedProgram: string;
  insurance: string;
  insuranceMemberId: string;
  referringProvider: string;
  referringFacility: string;
  referringPhone: string;
  referringFax: string;
}

// ==================== CONTEXT ====================

export interface FaxIntakeFormData {
  fileName: string;
  fileUrl: string;
  patientName: string;
  dob: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  diagnosisLabel: string;
  diagnosisCode: string;
  requestedProgram: string;
  insurance: string;
  insuranceMemberId: string;
  referringProvider: string;
  referringFacility: string;
  referringPhone: string;
  referringFax: string;
}

export interface TransitionDemoUser {
  id: string;
  name: string;
  role: string;
  access: ('transition' | 'operate')[];
}

export interface TransitionContextValue {
  referrals: Referral[];
  getById: (id: string) => Referral | undefined;
  acceptReferral: (id: string) => void;
  declineReferral: (id: string, reason: string) => void;
  overrideDecision: (id: string, outcome: DecisionOutcome, reason: string) => void;
  beginProcessing: (id: string) => void;
  returnToProcessing: (id: string, note: string, tasks: ReprocessingTask[]) => void;
  simulateReprocessingComplete: (id: string) => void;
  createPhoneReferral: (data: PhoneIntakeFormData) => string;
  createFaxReferral: (data: FaxIntakeFormData) => string;
  simulateIntake: (source: ReferralSource) => void;
  simulateMissingInfoResponse: (referralId: string) => void;
  simulateExecution: (referralId: string) => void;
  simulateIncomingDocument: (referralId: string) => void;
  overrideAutoDecline: (id: string, reason: string) => void;
  simulateOutreachSent: (id: string) => void;
  resetReferral: (id: string) => void;
  resetAll: () => void;
  // Attention tracking — unseen referrals that need coordinator action
  unseenAttentionIds: Set<string>;
  markAsSeen: (id: string) => void;
  // Demo personas
  demoUsers: TransitionDemoUser[];
  currentDemoUser: TransitionDemoUser;
  switchDemoUser: (user: TransitionDemoUser) => void;
}
